import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Spin } from "antd";
import { server } from "../../../../api";
import { resolveImageUrl } from "../../FloorPlanModal/utils/imageUtils";
import StaticStation from "./StaticStation";
import StaticSymbol from "./StaticSymbol";
import PersonnelMarker from "./PersonnelMarker";
import PersonnelCluster from "./PersonnelCluster";
import EquipmentLayer, {
  renderEquipmentZones,
  renderEquipmentBodies,
} from "./EquipmentLayer";
import LineBackground from "./LineBackground";
import TunnelBackground from "./TunnelBackground";
import GeoBackdrop from "./GeoBackdrop";
import ZoomControls from "./ZoomControls";
import CanvasRail from "./CanvasRail";
import BlankCanvasHint from "./BlankCanvasHint";
import FloorPaint from "./FloorPaint";
import { fitOffSiteView, splitOffSite } from "../utils/offSite";
import { getSymbolById } from "../../FloorPlanModal/utils/symbolTypes";
import { STATION_HALF_SIZE } from "../../FloorPlanModal/utils/constants";
import {
  calculateScale,
  createViewport,
} from "../../FloorPlanModal/utils/viewport";
import { getStationColor } from "../../FloorPlanModal/utils/stationUtils";
import { projectShading } from "../../FloorPlanModal/utils/tripwireUtils";
import { getTripwireColor } from "../../FloorPlanModal/utils/tripwireColors";
import { useViewTransform } from "../hooks/useViewTransform";
import { clusterPersonnel } from "../utils/personnelClustering";
import {
  CLUSTER_HALF_PX,
  MARKER_HALF_PX,
  markerBox,
  resolveLabelVisibility,
} from "../utils/labelCollision";

// StaticStation positions its glyph with a plain CSS `translate(x, y)` (no
// -50%/-50% centering), i.e. it treats the given position as the glyph's
// TOP-LEFT corner, not its center. The old projection baked in a
// -STATION_HALF_SIZE offset that happened to also serve as this corner
// conversion; viewport.project returns the TRUE (center) position (see
// FloorPlanModal/utils/viewport.js), so that conversion has to be applied
// explicitly here instead, or every station renders STATION_HALF_SIZE px
// down-right of where it should. (StaticSymbol needs the same kind of
// conversion but at its own, different glyph size -- it does that
// conversion itself, see StaticSymbol.jsx.)
const toCornerPosition = (position) => ({
  x: (position?.x ?? 0) - STATION_HALF_SIZE,
  y: (position?.y ?? 0) - STATION_HALF_SIZE,
});

// Fixed SCREEN-pixel depth for a tripwire's shaded restricted-side gradient
// (2026-09-22(c) spec: "fixed pixel depth regardless of zoom"). See
// renderTripwires' comment -- x1/y1/x2/y2 there already include zoom/pan, so
// this constant composes correctly with no further scaling.
const TRIPWIRE_SHADING_DEPTH_PX = 32;

// The paint decision moved to FloorPaint with the paint; still exported here.
export { floorPaint } from "./FloorPaint";

const LiveCanvas = ({
  canvasRef,
  machineID,
  dimensions,
  canvasSize,
  layoutType,
  imageUrl,
  personnel,
  baseStations,
  equipment = [],
  equipmentVisible = true,
  tripwires = [],
  placedSymbols,
  symbolsVisible,
  stationsVisible,
  selectedPersonnelId,
  onPersonnelClick,
  onViewTransformChange,
  loading,
  // Map/plan layer state from useMapLayers (LayerControls.jsx). Absent =
  // no map: the plan at full opacity, as before.
  layers,
  // The host's 圖層 control (LayerControls), placed on the top-right rail.
  layerControl,
  // Bumped by the host's 場外 title status: each new value fits the site and
  // every off-site item into view (fitOffSiteView). 0 = never asked.
  fitOffSiteKey = 0,
}) => {
  const mapVisible = !!layers?.mapVisible;
  const planVisible = layers?.planVisible ?? true;
  const planOpacity = layers?.planOpacity ?? 1;
  const gridVisible = layers?.gridVisible ?? true;
  const [selectedStationId, setSelectedStationId] = useState(null);
  // Machines are click-to-open-popover, not click-to-focus (ADDENDUM F1),
  // so this is popover state -- it lives here for the same reason
  // selectedStationId does, not in LiveDashboardCore's dashboard state.
  const [openEquipmentId, setOpenEquipmentId] = useState(null);
  // Which personnel cluster's member list is open, and which marker the mouse
  // is over (hover forces a suppressed name label back). Both are canvas-local
  // presentation state, like selectedStationId above.
  const [openClusterKey, setOpenClusterKey] = useState(null);
  const [hoveredPersonnelId, setHoveredPersonnelId] = useState(null);

  // ZOOM/PAN. Only the plane layout gets it: "line" and "tunnel" pin their
  // markers to fixed lanes (viewport.place snaps y to H/2 on a line and to
  // the two lane fractions of H on a tunnel, and TunnelBackground/StaticSymbol's
  // vertical_line bar draw against the same fractions), so those layouts have
  // no metre-space geometry to zoom into -- only their x is a real
  // measurement. Zooming them would need that lane geometry reworked first.
  // It also gates on the scale being real -- see the READINESS SIGNAL note in
  // utils/viewport.js -- so no gesture can move a view built on scale 1.
  const zoomEnabled =
    layoutType === "plane" &&
    calculateScale(dimensions, canvasSize, layoutType).isValid;

  const {
    containerRef,
    zoom,
    pan,
    isTransformed,
    isDragging,
    atMinZoom,
    atMaxZoom,
    reset: resetView,
    showView,
    zoomByStep,
    handleMouseDown,
    handleClickCapture,
  } = useViewTransform({
    dimensions,
    canvasSize,
    layoutType,
    enabled: zoomEnabled,
    resetKey: machineID,
    onTransformChange: onViewTransformChange,
  });

  // The one viewport every pixel below comes from. Built HERE rather than
  // taken as a prop because zoom/pan are this component's own state: the
  // hosts' data hooks hold metres and must not know the view has moved (that
  // is what keeps a 2s poll from disturbing the view).
  const viewport = useMemo(
    () => createViewport({ dimensions, canvasSize, layoutType, zoom, pan }),
    [dimensions, canvasSize, layoutType, zoom, pan],
  );

  // isValid -- NOT pxPerMeter > 0 -- is the readiness signal: pxPerMeter
  // falls back to 1 * zoom, not 0, while the floor plan is still loading.
  const viewportValid = viewport.isValid;

  // Floor-plan image box, in canvas pixels. This is what makes the image
  // track the viewport instead of being pinned to the canvas box.
  const floorBox = viewport.floorRect;

  // Positions live in METRES in state (see hooks/useLiveData.js); every pixel
  // on this canvas is produced here, at render time, from the current
  // viewport. That is what lets a resize -- and the zoom/pan above -- be a
  // plain re-render instead of a state rewrite.
  //
  // Stations and personnel go through viewport.place: on a plane that is the
  // plain projection, on a line it pins y to the strip's centre, on a tunnel
  // it snaps to the nearer lane. Both hosts get it identically by
  // construction.
  const projectMarker = viewport.place;

  // Off-site people and machines (GPS fixes outside the site) are always
  // drawn on a plane, map or not (D-29); a line/tunnel strip has no space
  // beside the site for them. The host's card title counts them.
  const offSiteDrawable = layoutType === "plane";
  // Someone with no position stays in the lists but is not drawn:
  // viewport.place would put a null position at the floor's origin corner.
  const shownPersonnel = splitOffSite(personnel, offSiteDrawable).shown.filter(
    (person) => person.position_meters,
  );
  const shownEquipment = splitOffSite(equipment, offSiteDrawable).shown;

  // Only a NEW key fits: a 2s poll re-renders with new data under the same
  // key and must never move the view.
  useEffect(() => {
    if (!fitOffSiteKey) return;
    showView(
      fitOffSiteView({
        items: [...(personnel || []), ...equipment],
        dimensions,
        canvasSize,
        layoutType,
      }),
    );
  }, [fitOffSiteKey]);

  // DECLUTTERING. Both passes work on the points AS PROJECTED BY THE CURRENT
  // VIEWPORT, i.e. in screen pixels with zoom already applied, so they compose
  // with zoom/pan instead of fighting it: zooming in widens the pixel gaps,
  // which splits clusters and frees up labels with no zoom-aware rule of their
  // own. See utils/personnelClustering.js and utils/labelCollision.js.
  //
  // The two refs carry the PREVIOUS result forward to supply hysteresis (the
  // anti-flicker mechanism -- personnel poll every 2s and the demo workers
  // move continuously). Writing them inside the memo is safe because both
  // helpers are idempotent when fed their own output: re-running with the same
  // points and the same previous result returns that same result.
  const previousGroupsRef = useRef([]);
  const previousLabelsRef = useRef([]);

  const personnelGroups = useMemo(() => {
    const points = shownPersonnel.map((person) => ({
      id: person.id,
      name: person.name,
      status: person.status,
      person,
      ...projectMarker(person.position_meters),
    }));
    const groups = clusterPersonnel({
      points,
      previousGroups: previousGroupsRef.current,
    });
    previousGroupsRef.current = groups;
    return groups;
  }, [shownPersonnel, projectMarker]);

  const visibleLabelIds = useMemo(() => {
    // Every marker on the canvas is an obstacle a label may not cover; only
    // single (unclustered) markers have a name label to place, since a cluster
    // shows its own count instead.
    const obstacles = personnelGroups.map((group) =>
      markerBox(group, group.count > 1 ? CLUSTER_HALF_PX : MARKER_HALF_PX),
    );
    const forcedIds = [selectedPersonnelId, hoveredPersonnelId].filter(
      (id) => id != null,
    );
    const visible = resolveLabelVisibility({
      labels: personnelGroups
        .filter((group) => group.count === 1)
        .map((group) => ({
          id: group.members[0].id,
          x: group.x,
          y: group.y,
          text: group.members[0].name,
          status: group.status,
        })),
      obstacles,
      forcedIds,
      previousVisibleIds: previousLabelsRef.current,
    });
    // A forced label is NOT remembered as a winner: it was shown because the
    // pointer was on it, not because it won any contest, and carrying that
    // into the sticky set would let a moment's hover permanently take a
    // neighbour's name away.
    previousLabelsRef.current = [...visible].filter(
      (id) => !forcedIds.includes(id),
    );
    return visible;
  }, [personnelGroups, selectedPersonnelId, hoveredPersonnelId]);

  const handleCanvasClick = () => {
    onPersonnelClick(null);
  };

  const protectionStations =
    stationsVisible && viewportValid
      ? baseStations.filter(
          (s) =>
            s.category === "防護區" &&
            s.radius_meters != null &&
            s.radius_meters > 0,
        )
      : [];

  const colors = getStationColor("防護區");

  const equipmentZones =
    equipmentVisible && viewportValid
      ? renderEquipmentZones({ equipment: shownEquipment, layoutType, viewport })
      : null;

  // Drawn after ALL zones so one machine's zone fill can never paint over
  // another machine's body line.
  const equipmentBodies =
    equipmentVisible && viewportValid
      ? renderEquipmentBodies({
          equipment: shownEquipment,
          layoutType,
          viewport,
        })
      : null;

  // Cross-line (越線) tripwires. Enabled lines draw in the shared active
  // colour; disabled ones draw in the shared inactive (grey) colour rather
  // than being hidden outright -- an operator watching the live dashboard
  // should still see that a gate exists there even while its alarm is
  // switched off, the same way a disabled protection-zone station would
  // look out of place if it just vanished. Both are SOLID (no dashing) per
  // the 2026-09-22(b) UX revision -- see FloorPlanModal/utils/tripwireColors.js
  // for the single source of truth these colours come from (shared with the
  // floor-plan editor overlay). Toggling ALL tripwires off is the caller's
  // job (no separate visibility toggle here, matching how equipment zones
  // behave). Shading gradient ids are namespaced by `machineID` (not just
  // `tw.id`): a single dashboard page can mount several LiveCanvas
  // instances at once, one per `live_position` chart slot, each for a
  // DIFFERENT machine (see DashboardView.js) -- without the machineID
  // prefix, two machines' tripwires that happen to share a numeric id would
  // collide on the same <linearGradient> id and one canvas would render the
  // other's shading colour/opacity.
  const renderTripwires = () => {
    if (!viewportValid || tripwires.length === 0) return null;
    return tripwires.map((tw) => {
      const [p1, p2] = tw.points;
      // One path for every layout: a lane gate is the segment [[x,0],[x,H]],
      // so projectPoints draws it as the full-height strip.
      const [[x1, y1], [x2, y2]] = viewport.projectPoints([p1, p2]);

      // Shaded restricted side(s): computed in metres and projected, drawn
      // UNDER the line. The depth is a fixed SCREEN pixel value at any zoom.
      const shading = projectShading(
        viewport,
        p1,
        p2,
        tw.direction,
        TRIPWIRE_SHADING_DEPTH_PX,
      );

      const color = getTripwireColor(tw.enabled);
      const lineOpacity = tw.enabled ? 0.9 : 0.6;
      return (
        <g key={`tripwire-${tw.id}`}>
          {shading.length > 0 && (
            <>
              <defs>
                {shading.map((s) => (
                  <linearGradient
                    key={`grad-${s.side}`}
                    id={`tripwire-shade-live-${machineID}-${tw.id}-${s.side}`}
                    gradientUnits="userSpaceOnUse"
                    x1={s.gradient.x1}
                    y1={s.gradient.y1}
                    x2={s.gradient.x2}
                    y2={s.gradient.y2}
                  >
                    <stop
                      offset="0%"
                      stopColor={color}
                      stopOpacity={s.opacityFrom * lineOpacity}
                    />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              {shading.map((s) => (
                <polygon
                  key={`shade-${s.side}`}
                  points={s.polygon.map((p) => p.join(",")).join(" ")}
                  fill={`url(#tripwire-shade-live-${machineID}-${tw.id}-${s.side})`}
                  pointerEvents="none"
                />
              ))}
            </>
          )}
          <line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={color}
            strokeWidth={2}
            opacity={lineOpacity}
          />
        </g>
      );
    });
  };

  const renderProtectionZones = () => {
    if (
      protectionStations.length === 0 &&
      !equipmentZones?.length &&
      !equipmentBodies?.length &&
      tripwires.length === 0
    ) {
      return null;
    }
    return (
      <svg
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
        aria-hidden="true"
      >
        {protectionStations.map((station) => {
          const { x: cx, y: cy } = viewport.place(station.position_meters);

          const r = station.radius_meters * viewport.pxPerMeter;
          return (
            <circle
              key={station.id}
              cx={cx}
              cy={cy}
              r={r}
              fill="rgba(250, 140, 22, 0.2)"
              stroke={colors.border}
              strokeWidth={1}
            />
          );
        })}
        {equipmentZones}
        {equipmentBodies}
        {renderTripwires()}
      </svg>
    );
  };

  return (
    <Spin spinning={loading}>
      <div
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onClickCapture={handleClickCapture}
        style={{
          position: "relative",
          width: `${canvasSize.width}px`,
          height: `${canvasSize.height}px`,
          border: "1px solid #d9d9d9",
          borderRadius: 4,
          // Clip everything to the canvas box. A tracked position is free to
          // leave the floor -- a machine's head/tail sit +-span/2 from a
          // centre that is only clamped at its own point, and real fixes can
          // land out of bounds too -- but nothing may be PAINTED outside the
          // border, or markers float over the surrounding card and read as
          // belonging to it. Popovers are unaffected: antd portals them to
          // document.body, and nothing here overrides getPopupContainer.
          overflow: "hidden",
          backgroundColor: "#ffffff",
          // Only while a pan is actually in progress: a permanent "grab"
          // cursor over the whole canvas would hide the pointer cue that
          // tells the operator a marker is clickable.
          ...(isDragging ? { cursor: "grabbing" } : {}),
        }}
      >
        {/* The floor-plan image is rendered here rather than through
            FloorPlanModal's CanvasImage because it has to TRACK THE VIEWPORT:
            CanvasImage is width/height 100% of the canvas box, which is a
            fixed screen rectangle, so under zoom the markers would slide
            across a stationary plan. Sized and offset from `floorBox` (the
            metre origin through the declared floor dimensions, projected), it
            scales and translates with everything else.

            PRE-EXISTING BUG INHERITED ON PURPOSE: `objectFit: contain` fits
            the image by ITS OWN aspect ratio, so a plan image whose aspect
            differs from the declared floor dimensions is letterboxed INSIDE
            the floor box and does not line up with the markers. That
            misalignment exists today (CanvasImage does the same thing against
            the canvas box) and is not this change's to fix -- but note the
            box it letterboxes inside is now the floor rather than the canvas,
            which is strictly closer to correct and is identical whenever the
            image aspect does match. */}
        {mapVisible && viewportValid && (
          <GeoBackdrop
            geoRef={layers.geoRef}
            tileLayers={layers.tileLayers}
            layerId={layers.layerId}
            opacity={layers.mapOpacity}
            viewport={viewport}
            canvasSize={canvasSize}
            onUnavailable={layers.markUnavailable}
          />
        )}

        {/* The ground the plan image sits on and fades into. */}
        <FloorPaint
          layer="ground"
          canvasSize={canvasSize}
          viewport={viewport}
          zoom={zoom}
          pan={pan}
          hasImage={!!imageUrl}
          mapVisible={mapVisible}
          planVisible={planVisible}
          planOpacity={planOpacity}
          gridVisible={gridVisible}
          plane={layoutType === "plane"}
        />

        {layoutType === "plane" && imageUrl && viewportValid && planVisible && (
          <img
            src={resolveImageUrl(imageUrl, server)}
            alt="平面圖"
            style={{
              position: "absolute",
              left: floorBox.left,
              top: floorBox.top,
              width: floorBox.width,
              height: floorBox.height,
              objectFit: "contain",
              objectPosition: "top left",
              pointerEvents: "none",
              userSelect: "none",
              ...(planOpacity < 1 ? { opacity: planOpacity } : {}),
            }}
            draggable={false}
          />
        )}

        {layoutType === "line" && (
          <LineBackground canvasSize={canvasSize} dimensions={dimensions} />
        )}

        {layoutType === "tunnel" && (
          <TunnelBackground
            canvasSize={canvasSize}
            dimensions={dimensions}
            viewport={viewport}
          />
        )}

        <FloorPaint
          layer="edge"
          canvasRef={canvasRef}
          canvasSize={canvasSize}
          viewport={viewport}
          zoom={zoom}
          pan={pan}
          hasImage={!!imageUrl}
          mapVisible={mapVisible}
          planVisible={planVisible}
          planOpacity={planOpacity}
          gridVisible={gridVisible}
          plane={layoutType === "plane"}
          onClick={handleCanvasClick}
        />

        {/* Zones are painted AFTER the grid canvas, not before it. On a plane
            layout with no floor-plan image the canvas fills itself with an
            OPAQUE #f0f2f5 grid background (see FloorPaint), and
            anything earlier in this stack is hidden underneath it — which
            silently blanked both the 防護區 circles and the 機具 alert zones on
            every plane site that had no image uploaded. The svg sets
            pointerEvents: none, so moving it above the canvas does not steal
            the canvas's click handling. */}
        {renderProtectionZones()}

        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        >
          {symbolsVisible &&
            placedSymbols &&
            placedSymbols
              .filter((symbol) => {
                const symbolType = getSymbolById(symbol.symbol_type);
                return symbolType?.compatibleLayouts?.includes(layoutType);
              })
              .map((symbol) => (
                <StaticSymbol
                  key={symbol.id || `symbol-${symbol.symbol_type}`}
                  symbol={symbol}
                  layoutType={layoutType}
                  position={
                    symbol.position_meters
                      ? viewport.place(symbol.position_meters)
                      : { x: 50, y: 50 }
                  }
                  viewport={viewport}
                />
              ))}
          {stationsVisible &&
            baseStations.map((station) => (
              <StaticStation
                key={station.id || `station-${station.device_id || station.name}`}
                station={station}
                layoutType={layoutType}
                position={toCornerPosition(
                  projectMarker(station.position_meters),
                )}
                selectedStationId={selectedStationId}
                setSelectedStationId={setSelectedStationId}
              />
            ))}
          <EquipmentLayer
            equipment={shownEquipment}
            layoutType={layoutType}
            viewport={viewport}
            visible={equipmentVisible}
            openEquipmentId={openEquipmentId}
            setOpenEquipmentId={setOpenEquipmentId}
          />
          {/* Ordered worst-status-LAST by clusterPersonnel, so an alarming
              worker paints on top of the pack rather than under it. sos and
              warning are never clustered, so every one of them is here as
              their own marker with their own pulse and click target. */}
          {personnelGroups.map((group) =>
            group.count > 1 ? (
              <PersonnelCluster
                key={group.key}
                group={group}
                isOpen={openClusterKey === group.key}
                setOpenClusterKey={setOpenClusterKey}
                onPersonnelClick={onPersonnelClick}
              />
            ) : (
              <PersonnelMarker
                key={group.members[0].id}
                person={group.members[0].person}
                position={group}
                isSelected={group.members[0].id === selectedPersonnelId}
                showLabel={visibleLabelIds.has(group.members[0].id)}
                ghost={!!group.members[0].person?.ghost}
                onHoverChange={setHoveredPersonnelId}
                onPersonnelClick={onPersonnelClick}
              />
            ),
          )}
        </div>

        <BlankCanvasHint layers={layoutType === "plane" ? layers : null} />

        {/* 圖層 top-right, the zoom pill top-left. The rails sit OUTSIDE the
            pointerEvents:none marker layer so their buttons are clickable,
            and late in the stack so they always paint above the markers. */}
        <CanvasRail>{layerControl}</CanvasRail>
        {zoomEnabled && (
          <CanvasRail side="left">
            <ZoomControls
              zoom={zoom}
              isTransformed={isTransformed}
              atMinZoom={atMinZoom}
              atMaxZoom={atMaxZoom}
              onZoomStep={zoomByStep}
              onReset={resetView}
            />
          </CanvasRail>
        )}
      </div>
    </Spin>
  );
};

export default LiveCanvas;
